import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  XOctagon,
  CheckCircle2,
  XCircle,
  FileCode
} from 'lucide-react';
import { closureApi } from '../../services/iso20022Api';
import {
  formatDate,
  getStatusBadgeClass,
  truncate,
  debounce
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const AccountClosures = () => {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [selectedClosure, setSelectedClosure] = useState(null);

  const loadClosures = async () => {
    try {
      setLoading(true);
      const data = await closureApi.getClosures(filters);
      console.log('[Load Closures] Count:', data?.length ?? 0);
      setClosures(data);
    } catch (error) {
      console.error('[Load Closures] Error:', error);
      setClosures([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClosures();
  }, [filters]);

  const handleFilterChange = debounce((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, 300);

  const clearFilters = () => {
    setFilters({
      status: '',
      search: ''
    });
  };

  const handleViewXml = (closure) => {
    setSelectedClosure(closure);
    setXmlModalOpen(true);
  };

  const getClosureStatusBadgeClass = (status) => {
    if (!status) return 'badge-info';
    
    const statusUpper = status.toUpperCase();
    
    if (statusUpper === 'COMPLETED' || statusUpper === 'SIGNED' || statusUpper === 'CLOSED') {
      return 'badge-success';
    }
    
    if (statusUpper === 'PROCESSING' || statusUpper === 'RECEIVED' || statusUpper === 'SENT' || statusUpper === 'PENDING') {
      return 'badge-warning';
    }
    
    if (statusUpper === 'REJECTED' || statusUpper === 'FAILED') {
      return 'badge-danger';
    }
    
    return 'badge-info';
  };

  const getClosureStatusText = (status) => {
    if (!status) return 'Unknown';
    
    const statusMap = {
      'SIGNED': 'Signed',
      'SENT': 'Sent',
      'RECEIVED': 'Received',
      'PROCESSING': 'Processing',
      'PENDING': 'Pending',
      'COMPLETED': 'Completed',
      'CLOSED': 'Closed',
      'REJECTED': 'Rejected',
      'FAILED': 'Failed'
    };
    
    return statusMap[status.toUpperCase()] || status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Account Closures</h1>
          <p className="text-gray-600 mt-1">View and manage account closure requests (acmt.019)</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadClosures}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Account ID, Account Name, Merchant Code..."
                  className="input pl-10"
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="CLOSED">Closed</option>
                <option value="PROCESSING">Processing</option>
                <option value="PENDING">Pending</option>
                <option value="RECEIVED">Received</option>
                <option value="SIGNED">Signed</option>
                <option value="SENT">Sent</option>
                <option value="REJECTED">Rejected</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Results ({closures.length})
            </h2>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading closures...</p>
              </div>
            </div>
          ) : closures.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <XOctagon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No closures found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Merchant Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Servicer
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signature
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {closures.map((closure) => (
                    <tr key={closure.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">
                          #{closure.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {truncate(closure.accountId, 20)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {closure.accountName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-600">
                          {closure.accountCurrency || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-600">
                          {closure.merchantCode || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-600">
                          {closure.accountServicerId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {closure.signatureOk === true ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-success-100" title="Signature valid">
                            <CheckCircle2 className="w-5 h-5 text-success-600" />
                          </span>
                        ) : closure.signatureOk === false ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-danger-100" title="Signature invalid">
                            <XCircle className="w-5 h-5 text-danger-600" />
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getClosureStatusBadgeClass(closure.status)}`}>
                          {getClosureStatusText(closure.statusLabel || closure.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(closure.createdAt, 'PPp')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Quick XML View - only show if rawXml exists */}
                          {closure.rawXml && (
                            <button
                              onClick={() => handleViewXml(closure)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                              title="View XML"
                            >
                              <FileCode className="w-4 h-4" />
                            </button>
                          )}
                          <Link
                            to={`/iso20022/closures/${closure.id}`}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center space-x-1"
                          >
                            <span>View</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* XML Viewer Modal */}
      {selectedClosure && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => {
            setXmlModalOpen(false);
            setSelectedClosure(null);
          }}
          xmlContent={selectedClosure.rawXml}
          fileName={`closure-acmt019-${selectedClosure.id}.xml`}
          title={`Account Closure #${selectedClosure.id}`}
        />
      )}
    </div>
  );
};

export default AccountClosures;
